import { addDays } from 'date-fns';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormRadioGroup } from '@/components/ui/radio-group';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

import { Conflicts } from '../campaign-details/conflicts';
import { getChannelDefaults } from '../utils/channel-defaults';
import { AudienceMessageForm } from './audience-message-form';
import { usePoolForm } from './use-pool-form';

export function CampaignSettingsCard() {
  const intl = useIntl();
  const { hookForm, poolOpeningsWithConflicts } = useJobPostingContext();
  const { conflicts, removeConflictingOpening } = usePoolForm();
  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
  } = hookForm;

  const isNeverEnding = watch('channels.pool.poolDetails.isNeverEnding');
  const campaignType = watch('channels.pool.poolDetails.campaignType');
  const poolOpenings = watch('channels.pool.poolDetails.openingMessages');
  const startsAt = watch('channels.pool.startsAt');
  const poolDisplayTitle = watch('channels.pool.displayTitle');

  const campaignTypeOptions = [
    {
      value: 'email',
      label: intl.formatMessage({
        defaultMessage: 'Email campaign',
        description: 'Email campaign type option',
      }),
      id: 'email',
    },
    {
      value: 'sms',
      label: intl.formatMessage({
        defaultMessage: 'SMS campaign',
        description: 'SMS campaign type option',
      }),
      id: 'sms',
    },
  ];

  // Check if all openings have conflicts
  const allOpeningsHaveConflicts = useMemo(
    () =>
      Object.keys(poolOpenings).every(
        id =>
          poolOpeningsWithConflicts && poolOpeningsWithConflicts.includes(id),
      ),
    [poolOpenings, poolOpeningsWithConflicts],
  );

  // Calculate minimum end date based on start date and channel constraints
  const minEndDate = useMemo(() => {
    if (!startsAt) return undefined;

    const { minDuration } = getChannelDefaults(POOL);

    const startDate = new Date(startsAt);
    return addDays(startDate, minDuration ?? 1);
  }, [startsAt]);

  const handleIsNeverEndingChange = (_checked: boolean | 'indeterminate') => {
    setValue('channels.pool.endAt', null, { shouldValidate: true });
  };

  const handleCampaignTypeChange = () => {
    Object.keys(poolOpenings).forEach(openingId => {
      setValue(
        `channels.pool.poolDetails.openingMessages.${openingId}.subject`,
        '',
      );
      setValue(
        `channels.pool.poolDetails.openingMessages.${openingId}.body`,
        '',
      );
    });
  };

  return (
    <div className="border-fx-neutral-200 mb-3 w-full rounded-lg border bg-white px-12 py-6 shadow-sm">
      <div className="flex flex-1 flex-col gap-6">
        <ChannelIcon
          channelSlug={POOL}
          variant="large"
          size="lg"
          className="mx-auto"
          channelName={getChannelName(POOL)}
          displayTitle={poolDisplayTitle}
        />

        {/* Campaign type */}
        <div className="flex flex-col gap-3">
          <Typography variant="subtitle2">
            <FormattedMessage
              defaultMessage="Campaign type"
              description="Label for campaign type selection"
            />
            <span className="text-fx-rose">*</span>
          </Typography>
          <FormRadioGroup
            control={control}
            name="channels.pool.poolDetails.campaignType"
            options={campaignTypeOptions}
            onChange={handleCampaignTypeChange}
          />
        </div>

        {/* From email */}
        {campaignType === 'email' && (
          <Input
            isNumber
            {...register('channels.pool.poolDetails.fromEmail')}
            label={intl.formatMessage({
              defaultMessage: 'From',
              description: 'Label for from email input',
            })}
            description={intl.formatMessage({
              defaultMessage:
                "Select the 'from' email address that your recipients will see.",
              description: 'Description for the from email input',
            })}
            placeholder={intl.formatMessage({
              defaultMessage:
                "Select the 'from' email address that your recipients will see.",
              description: 'Placeholder for the from email input',
            })}
            error={errors?.channels?.pool?.poolDetails?.fromEmail?.message}
            required
            readOnly // TODO: This should be removed once this ticket is implemented - https://linear.app/fountain/issue/AXR-888/integrate-senders-api-and-surface-from-email-selection-for-pool
          />
        )}

        <div className="flex gap-6">
          {/* Campaign start date */}
          <div className="flex-1">
            <FormDatePicker
              control={control}
              name="channels.pool.startsAt"
              label={intl.formatMessage({
                defaultMessage: 'Start date',
                description:
                  'Label for campaign start on the job posting flow card',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter start date',
                description: 'Placeholder for the start date input',
              })}
              error={errors?.channels?.pool?.startsAt?.message}
              required
            />
          </div>

          {/* Campaign end date */}
          <div className="relative flex-1">
            <FormDatePicker
              isEndOfDay
              control={control}
              name="channels.pool.endAt"
              label={intl.formatMessage({
                defaultMessage: 'End date',
                description:
                  'Pool campaign: when the re-engagement send window stops',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter end date',
                description: 'Placeholder for the end date input',
              })}
              error={errors?.channels?.pool?.endAt?.message}
              isDisabled={isNeverEnding}
              minDate={minEndDate}
              required
            />
            <Controller
              control={control}
              name="channels.pool.poolDetails.isNeverEnding"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={checked => {
                    field.onChange(checked);
                    handleIsNeverEndingChange(checked);
                  }}
                  label={intl.formatMessage({
                    defaultMessage: 'Never expires',
                    description: 'Label for never ending checkbox',
                  })}
                  className="absolute top-0 right-0"
                  labelVariant="small1"
                  labelClassName="!text-fx-neutral-600"
                />
              )}
            />
          </div>
        </div>

        {/* Audience and message details - displayed within the campaign settings card for single opening. Displaed as a separate section for multiple openings. */}
        {Object.keys(poolOpenings).length === 1 && (
          <>
            <div className="bg-fx-neutral-200 h-px w-full" />
            <AudienceMessageForm openingId={Object.keys(poolOpenings)[0]} />
          </>
        )}

        <Conflicts
          allOpeningsHaveConflicts={allOpeningsHaveConflicts}
          openingsWithConflicts={poolOpeningsWithConflicts}
          removeConflictingOpening={removeConflictingOpening}
          conflicts={conflicts}
        />
      </div>
    </div>
  );
}
